import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import api from "../api";
import styles from "./AddRoomModal.module.css";

const AddRoomModal = ({ isOpen, onClose, propertyId, onRoomAdded }) => {
  const [formData, setFormData] = useState({
    name: "",
    price: "",
    key_number: "",
    type: "",
    head_guardian: false,
    is_couple: false,
    exclude_display: false,
    property_id: propertyId,
    position: 0,
  });

  const [loading, setLoading] = useState(false);
  const [formLoading, setFormLoading] = useState(true);
  const [roomTypes, setRoomTypes] = useState([]);

  useEffect(() => {
    if (isOpen && propertyId) {
      fetchRoomTypes();
      fetchRoomCount();
    }
  }, [isOpen, propertyId]);

  const fetchRoomTypes = async () => {
    try {
      setFormLoading(true);
      const response = await api.get('/rooms/types');

      if (response.data.success) {
        setRoomTypes(response.data.data);
      } else {
        toast.error('Failed to load room types');
        // Fallback to hardcoded types
        setRoomTypes([
          { id: 1, name: 'Guardian', value: 'Guardian' },
          { id: 2, name: 'Maintenance', value: 'Maintenance' }
        ]);
      }
    } catch (error) {
      console.error('Error fetching room types:', error);
      toast.error('Failed to load room types');
      // Fallback to hardcoded types
      setRoomTypes([
        { id: 1, name: 'Guardian', value: 'Guardian' },
        { id: 2, name: 'Maintenance', value: 'Maintenance' }
      ]);
    } finally {
      setFormLoading(false);
    }
  };

  const fetchRoomCount = async () => {
    try {
      const response = await api.get(`/rooms/properties/${propertyId}`);
      if (response.data.success && response.data.data) {
        const roomCount = response.data.data.length;
        setFormData((prev) => ({
          ...prev,
          position: roomCount
        }));
      }
    } catch (error) {
      console.error('Error fetching room count:', error);
      // Set position to 0 as fallback
      setFormData((prev) => ({
        ...prev,
        position: 0
      }));
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    console.log(formData);

    if (!formData.name.trim()) {
      toast.error("Room name is required");
      return;
    }

    if (!formData.type) {
      toast.error("Room type is required");
      return;
    }

    try {
      setLoading(true);

      const response = await api.post(
        `/rooms/properties/${propertyId}`,
        formData
      );

      if (response.data.success) {
        toast.success("Room created successfully!");
        onRoomAdded(response.data.data);
        handleClose();
      } else {
        toast.error(response.data.message || "Failed to create room");
      }
    } catch (error) {
      console.error("Error creating room:", error);
      const errorMessage =
        error.response?.data?.message || "Failed to create room";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      name: "",
      price: "",
      key_number: "",
      type: "",
      head_guardian: false,
      is_couple: false,
      exclude_display: false,
      property_id: propertyId,
      position: 0,
    });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modal}>
      <div className={styles.modalDialog}>
        <div className={styles.modalContent}>
          <div className={styles.modalHeader}>
            <h4 className={styles.modalTitle}>Add room to property</h4>
            <button
              type="button"
              className={styles.close}
              onClick={handleClose}
            >
              &times;
            </button>
          </div>
          <div className={styles.modalBody}>
            {formLoading ? (
              <div className={styles.textCenter}>
                <div className={styles.spinnerBorder} role="status">
                  <span className="sr-only">Loading...</span>
                </div>
                <p>Loading form data...</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                <input type="hidden" name="property_id" value={propertyId} />

                <fieldset className={styles.formHorizontal}>
                  {/* Room name (or number) */}
                  <div className={styles.formGroup}>
                    <label
                      className={`${styles.controlLabel} ${styles.colSm4} ${styles.labelRequired}`}
                    >
                      Number / Name
                    </label>
                    <div className={styles.colSm5}>
                      <input
                        type="text"
                        name="name"
                        className={styles.formControl}
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                  </div>

                  {/* Price */}
                  <div className={styles.formGroup}>
                    <label
                      className={`${styles.controlLabel} ${styles.colSm4}`}
                    >
                      Price
                    </label>
                    <div className={styles.colSm5}>
                      <input
                        type="number"
                        name="price"
                        className={styles.formControl}
                        value={formData.price}
                        onChange={handleInputChange}
                        step="0.01"
                        min="0"
                      />
                    </div>
                  </div>

                  {/* Key number */}
                  <div className={styles.formGroup}>
                    <label
                      className={`${styles.controlLabel} ${styles.colSm4}`}
                    >
                      Number of keys
                    </label>
                    <div className={styles.colSm5}>
                      <input
                        type="number"
                        name="key_number"
                        className={styles.formControl}
                        value={formData.key_number}
                        onChange={handleInputChange}
                        min="1"
                      />
                    </div>
                  </div>

                  {/* Head Guardian */}
                  <div className={styles.formGroup}>
                    <label
                      className={`${styles.controlLabel} ${styles.colSm4}`}
                    >
                      Head Guardian
                    </label>
                    <div className={styles.colSm5}>
                      <input
                        type="checkbox"
                        name="head_guardian"
                        id="head_guardian"
                        className={styles.checkbox}
                        checked={formData.head_guardian}
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>

                  {/* Is for couple? */}
                  <div className={styles.formGroup}>
                    <label
                      className={`${styles.controlLabel} ${styles.colSm4}`}
                    >
                      Is for couple?
                    </label>
                    <div className={styles.colSm5}>
                      <input
                        type="checkbox"
                        name="is_couple"
                        id="is_couple"
                        className={styles.checkbox}
                        checked={formData.is_couple}
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>

                  {/* Exclude from displaying on the GG website? */}
                  <div className={styles.formGroup}>
                    <label
                      className={`${styles.controlLabel} ${styles.colSm4}`}
                    >
                      Exclude from display on the website
                    </label>
                    <div className={styles.colSm5}>
                      <input
                        type="checkbox"
                        name="exclude_display"
                        id="exclude_display"
                        className={styles.checkbox}
                        checked={formData.exclude_display}
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>

                  {/* Type */}
                  <div className={styles.formGroup}>
                    <label
                      className={`${styles.controlLabel} ${styles.colSm4} ${styles.labelRequired}`}
                    >
                      Type
                    </label>
                    <div className={styles.colSm5}>
                      <select
                        name="type"
                        className={styles.formControl}
                        value={formData.type}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="">Select room type</option>
                        {roomTypes.map((type) => (
                          <option key={type.id} value={type.value}>
                            {type.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </fieldset>

                <fieldset className={styles.formHorizontal}>
                  <div className={`${styles.formGroup} ${styles.textRight}`}>
                    <div className={styles.colSm4}></div>
                    <div className={styles.colSm5}>
                      <button
                        type="submit"
                        className={`${styles.btn} ${styles.btnHead} ${styles.btnStandard}`}
                        disabled={loading}
                      >
                        {loading ? (
                          <>
                            <span
                              className={`${styles.spinnerBorder} mr-2`}
                              role="status"
                              aria-hidden="true"
                            ></span>
                            Creating...
                          </>
                        ) : (
                          <>
                            <i className="glyphicon glyphicon-plus"></i> Add
                            Room
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </fieldset>
              </form>
            )}
          </div>
          <div className={styles.modalFooter}>
            <button
              type="button"
              className={`${styles.btn} ${styles.btnDefault}`}
              onClick={handleClose}
            >
              Close
            </button>
          </div>
        </div>
      </div>
      <div className={styles.modalBackdrop}></div>
    </div>
  );
};

export default AddRoomModal;
